import {
  LoaderSpinnerModal,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';
import React, { useState } from 'react';
import { fetchEmissions } from '../fetch-data/fetch-emissions-data';
import { getStringifiedServiceConfigsServiceConfig } from '../services/get-stringified-service-configs-service-config';
import { useFeatureAppConfig } from '../services/use-feature-app-config';
import { useFeatureServices } from '../services/use-feature-services';
import { EmissionsModel } from '../typings/general';
import { BaseInformationSection } from './base-information';
import { EmissionsInput } from './emissions-input';
import InputLabel from './input-label';
import { ResultsSection } from './results-section';
import { EmissionsAppContainer, LabelWrapper } from './styled';
import { AppConstants } from '../utils/app-constants';
import { useTrackingManager } from '../services/use-tracking-manager';

export const EmissionsApp: React.FC = () => {
  const trackingManager = useTrackingManager();
  const { faServicesUrl, mockIds, mocksBaseUrl } = useFeatureAppConfig();
  const {
    'service-config-provider': serviceConfigProvider,
    's2:server-request': serverRequest,
  } = useFeatureServices();

  const serviceConfigsServiceConfig = getStringifiedServiceConfigsServiceConfig(
    serviceConfigProvider,
    serverRequest,
  );

  interface ErrorRequestModel {
    state: boolean;
    message: string;
    vin?: string;
  }

  const [errorRequest, setErrorRequest] = useState<ErrorRequestModel>({
    state: false,
    message: '',
  });
  const [emissionsData, setEmissionsData] = useState<
    EmissionsModel | undefined
  >();
  const [pendingFetch, setPendingFetch] = useState(false);

  const getEmissionData = (vin: string): void => {
    setPendingFetch(true);
    fetchEmissions({
      vin,
      faServicesUrl,
      serviceConfigsServiceConfig: serviceConfigsServiceConfig as string,
      mockIds: mockIds as string,
      mocksBaseUrl: mocksBaseUrl as string,
    })
      .then((emissions: EmissionsModel) => {
        setPendingFetch(false);
        if (emissions && emissions.status) {
          if (emissions.make === 'Audi') {
            setErrorRequest({
              state: true,
              message: AppConstants.invalidVinAudiMessage,
              vin: emissions.vin,
            });
          } else {
            setEmissionsData(emissions);
            setErrorRequest({
              state: false,
              message: '',
              vin: emissions.vin,
            });
            trackingManager.trackEmissionsGlobalEvent(
              'VWBasic_VINFormSubmissionSuccessMessage_Load',
              {
                vin,
                formType: 'otherform',
                formVersion: '1',
                formName: 'Emission Request',
              },
              undefined,
            );
          }
        } else {
          if (emissions.errorCode && emissions.errorCode == '93') {
            setErrorRequest({
              state: true,
              message: AppConstants.invalidVinCanadianMessage,
              vin: emissions.vin,
            });
          } else {
            setErrorRequest({
              state: true,
              message: AppConstants.invalidVinMessage,
              vin: emissions.vin,
            });
          }

          trackingManager.trackEmissionsGlobalEvent(
            'VWBasic_VINFormErrorMessage_Load',
            {
              vin,
              formType: 'otherform',
              formVersion: '1',
              formName: 'Emission Request',
              message: 'This VIN is not part of Volkswagen Emission Program',
              code: '400',
            },
            undefined,
          );
        }
      })
      .catch(() => {
        setPendingFetch(false);
        setErrorRequest({
          state: true,
          message: 'There has been a configuration error. Please try again.',
          vin: vin,
        });
        trackingManager.trackEmissionsGlobalEvent(
          'VWBasic_VINFormErrorMessage_Load',
          {
            vin,
            formType: 'otherform',
            formVersion: '1',
            formName: 'Emission Request',
            message: 'There has been a configuration error',
            code: '400',
          },
          undefined,
        );
      });
  };

  return (
    <div id="emissionsFA">
      <EmissionsAppContainer>
        <InputLabel />
        <EmissionsInput
          onSubmitHandler={getEmissionData}
          errorRequest={errorRequest}
        />
        <LabelWrapper>
          <TextCustom
            appearance={TextAppearance.headline200}
            style={{
              color: TextColor.secondary,
              fontSize: TextSize.TextSize12,
              lineHeight: TextLineHeight.TextLineHeight18,
              fontWeight: TextWeight.regular,
            }}
          >
            *Please note that the VIN (Vehicle Identification Number) does not
            use the letters “O,” “I,” or “Q.”
          </TextCustom>
        </LabelWrapper>
        {!errorRequest.state &&
        emissionsData &&
        emissionsData.vin !== '00000000000000000' ? (
          <>
            <ResultsSection emissionsData={emissionsData} />
            <BaseInformationSection
              dataLoaded={true}
              emissionsData={emissionsData}
            />
          </>
        ) : (
          <BaseInformationSection />
        )}
      </EmissionsAppContainer>
      {pendingFetch && <LoaderSpinnerModal />}
    </div>
  );
};
